import { Button } from "@dropbox/dig-components/buttons";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { SyncingLine } from "@dropbox/dig-icons/assets";
import {
  getLastPeopleRecommendationsSyncMs,
  getOrgChart,
  getPeopleRecommendations,
  syncPeopleRecommendations,
} from "@mirage/service-typeahead-search";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import * as React from "react";
import { useEffect, useState } from "react";

export const PeopleDeveloperSettings: React.FC = () => {
  const [peopleCacheLength, setPeopleCacheLength] = useState<
    | {
        peopleRecommendationsLength: number;
        orgChartLength: number;
      }
    | undefined
  >(undefined);
  const [lastPeopleRecommendationsSyncMs, setLastPeopleRecommendationsSyncMs] =
    useState<number | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    Promise.all([
      getPeopleRecommendations(),
      getOrgChart(),
      getLastPeopleRecommendationsSyncMs(),
    ])
      .then(([recommendations, orgChart, timestamp]) => {
        setPeopleCacheLength({
          peopleRecommendationsLength: recommendations.length,
          orgChartLength: orgChart.length,
        });
        if (timestamp) {
          setLastPeopleRecommendationsSyncMs(timestamp);
        }
      })
      .catch((error) => {
        setError(error.message);
      });
  }, []);

  const resetPeopleCache = () => {
    setPeopleCacheLength(undefined);
    setLastPeopleRecommendationsSyncMs(undefined);
    setError(undefined);

    syncPeopleRecommendations()
      .then(({ peopleRecommendationsLength, orgChartLength, timestamp }) => {
        setPeopleCacheLength({
          peopleRecommendationsLength,
          orgChartLength,
        });
        if (timestamp) {
          setLastPeopleRecommendationsSyncMs(timestamp);
        }
      })
      .catch((error) => {
        setError(error.message);
      });
  };

  return (
    <Box display="flex" alignItems="center" marginTop="8" columnGap="20">
      <Text isBold>People:</Text>
      {error ? (
        <Text color="error">{error}</Text>
      ) : peopleCacheLength ? (
        <Text>
          {peopleCacheLength.peopleRecommendationsLength.toLocaleString()}{" "}
          cached for typeahead,{" "}
          {peopleCacheLength.orgChartLength.toLocaleString()} cached for org
          chart
        </Text>
      ) : (
        <Text color="faint">Syncing...</Text>
      )}
      {peopleCacheLength && lastPeopleRecommendationsSyncMs && (
        <Text color="faint">
          (last synced{" "}
          {new Date(lastPeopleRecommendationsSyncMs).toLocaleString(undefined, {
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })}
          )
        </Text>
      )}
      <Box display="flex" columnGap="12">
        <DigTooltip title="Force people cache to re-sync">
          <Button
            variant="opacity"
            onClick={resetPeopleCache}
            withIconStart={<UIIcon size="small" src={SyncingLine} />}
          >
            Resync
          </Button>
        </DigTooltip>
      </Box>
    </Box>
  );
};
